import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Lock, CheckCircle2 } from 'lucide-react'
import { useAuth } from '@/application/auth'
import { getErrorMessage } from '@/lib/api'
import { changeMyPassword } from '@/application/users/user.service'
import { toast } from '@/lib/toast'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Card, CardHeader, CardContent, CardFooter } from '@/presentation/components/ui/Card'
import { LoginLogo } from '@/presentation/components/ui/AppLogo'
import { env } from '@/config/env'

// =============================================================================
// Schema
// =============================================================================

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Ingresa tu contraseña temporal'),
    newPassword: z
      .string()
      .min(8, 'Mínimo 8 caracteres')
      .regex(/[A-Z]/, 'Debe incluir al menos una mayúscula')
      .regex(/[a-z]/, 'Debe incluir al menos una minúscula')
      .regex(/[0-9]/, 'Debe incluir al menos un número'),
    confirmPassword: z.string().min(1, 'Confirma tu nueva contraseña'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })

type FormData = z.infer<typeof schema>

// =============================================================================
// Password strength indicator
// =============================================================================

function PasswordStrength({ password }: { password: string }) {
  if (!password) return null

  const checks = [
    { label: '8+ caracteres', ok: password.length >= 8 },
    { label: 'Mayúscula', ok: /[A-Z]/.test(password) },
    { label: 'Minúscula', ok: /[a-z]/.test(password) },
    { label: 'Número', ok: /[0-9]/.test(password) },
  ]

  const score = checks.filter((c) => c.ok).length
  const barColor =
    score <= 1 ? 'bg-[var(--destructive)]' :
    score === 2 ? 'bg-[var(--color-warning)]' :
    score === 3 ? 'bg-[var(--color-warning)]' :
    'bg-[var(--color-success)]'

  return (
    <div className="space-y-2 mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors duration-200 ${
              i <= score ? barColor : 'bg-[var(--border)]'
            }`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {checks.map((c) => (
          <span
            key={c.label}
            className="text-xs flex items-center gap-1"
            style={{ color: c.ok ? 'var(--color-success)' : 'var(--foreground-muted)' }}
          >
            <CheckCircle2 className={`h-3 w-3 ${c.ok ? 'opacity-100' : 'opacity-30'}`} />
            {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}

// =============================================================================
// Page Component
// =============================================================================

export function ForceChangePasswordPage() {
  const { user, onPasswordChanged } = useAuth()
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const newPassword = watch('newPassword', '')

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    try {
      // Se pasa por el servicio, que ademas guarda la sesion nueva que el
      // servidor devuelve. Sin ella el navegador seguiria usando el token
      // anterior, que todavia dice que hay que cambiar la contrasena, y el
      // panel recibiria 403 en cada peticion.
      await changeMyPassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })

      toast.success('Contraseña actualizada correctamente')
      onPasswordChanged()
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo cambiar la contraseña'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen alto-de-pantalla flex items-center justify-center bg-gradient-to-br from-[var(--color-primary-950)] via-[var(--color-primary-900)] to-[var(--color-primary-800)] p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 neural-bg opacity-50" />

      {/* Main Card */}
      <Card className="w-full max-w-md relative z-10 animate-fade-in">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <LoginLogo subtitle="Cambio de contraseña requerido" />
          </div>
          <div>
            <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
              Por seguridad debes establecer una contraseña personal.
              {user?.email && (
                <> Accediendo como <strong style={{ color: 'var(--foreground)' }}>{user.email}</strong>.</>
              )}
            </p>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {/* Notice */}
            <div
              className="p-3 rounded-lg text-sm"
              style={{
                background: 'var(--destructive)',
                opacity: 0.85,
                color: 'var(--destructive-foreground)',
              }}
            >
              Esta cuenta fue creada con una contraseña temporal. Debes cambiarla para continuar.
            </div>

            {/* Current password */}
            <Input
              {...register('currentPassword')}
              type={showCurrent ? 'text' : 'password'}
              label="Contraseña temporal actual"
              placeholder="••••••••"
              error={errors.currentPassword?.message}
              leftIcon={<Lock className="h-4 w-4" />}
              rightIcon={
                <button type="button" onClick={() => setShowCurrent((v) => !v)} tabIndex={-1}
                  className="hover:text-[var(--foreground)] transition-colors">
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
              autoComplete="current-password"
            />

            {/* New password */}
            <div>
              <Input
                {...register('newPassword')}
                type={showNew ? 'text' : 'password'}
                label="Nueva contraseña"
                placeholder="••••••••"
                error={errors.newPassword?.message}
                leftIcon={<Lock className="h-4 w-4" />}
                rightIcon={
                  <button type="button" onClick={() => setShowNew((v) => !v)} tabIndex={-1}
                    className="hover:text-[var(--foreground)] transition-colors">
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                }
                autoComplete="new-password"
              />
              <PasswordStrength password={newPassword} />
            </div>

            {/* Confirm password */}
            <Input
              {...register('confirmPassword')}
              type={showConfirm ? 'text' : 'password'}
              label="Confirmar nueva contraseña"
              placeholder="••••••••"
              error={errors.confirmPassword?.message}
              leftIcon={<Lock className="h-4 w-4" />}
              rightIcon={
                <button type="button" onClick={() => setShowConfirm((v) => !v)} tabIndex={-1}
                  className="hover:text-[var(--foreground)] transition-colors">
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
              autoComplete="new-password"
            />
          </CardContent>

          <CardFooter>
            <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
              Establecer nueva contraseña
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-4 text-center text-[var(--color-primary-300)] text-sm">
        <p>&copy; {new Date().getFullYear()} {env.APP_NAME}. Todos los derechos reservados.</p>
        <p className="text-xs mt-1 opacity-70">v{env.APP_VERSION}</p>
      </div>
    </div>
  )
}
